import { CANCELABLE_ERROR_CODES, DISPLAY_STATUS, type DisplayStatusType, type CountryCode, type ProjectCode } from '@constants'
import { useDialog } from '@dni-hooks/useDialog'
import { type ResponseErrorGenericsV2 } from '@dni-types/utilities/response'
import { mutateAllECommerceOrders } from '@hooks-api/ec/useApiECommerceOrders'
import { useECommerceGetPaymentTransactionCancelable } from '@hooks-api/eCommerceV2/useApiECommerceGetPaymentTransactionCancelable'
import { useApiCancelPaymentTransaction, useECommerceCancelPaymentTransaction } from '@hooks-api/fortune/useApiCancelPaymentTransaction'
import { useApiGetPaymentTransactionCancelable } from '@hooks-api/fortune/useApiGetPaymentTransactionCancelable'
import { type SnackbarVariantsProps } from '@pets-ui/Snackbar/SnackbarVariants'
import { useSnackbar } from '@pets-ui/Snackbar/useSnackbar'
import { wordPressNavigate } from '@utils'
import { AxiosError } from 'axios'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom-v5'
import { DIALOG_CONFIRM_CANCEL_ORDER, type DialogConfirmCancelOrderExtraProps } from './DialogConfirmCancelOrder'
import { DIALOG_MANUAL_REFUND_REQUIRED, type DialogManualRefundRequiredExtraProps } from './DialogManualRefundRequired'
import { DIALOG_READY_FOR_SHIPMENT_CANNOT_CANCEL, type DialogReadyForShipmentCannotCancelExtraProps } from './DialogReadyForShipmentCannotCancel'
import { DIALOG_UNSUPPORTED_SELF_CANCEL, type DialogUnsupportedSelfCancelExtraProps } from './DialogUnsupportedSelfCancel'

interface UseCancelOrderParams {
  environment: 'campaign' | 'merchant' | 'wordPress'
  countryCode: CountryCode
  projectCode?: ProjectCode
  campaignCodeFromUrl: string
  onCancelOrderClick?: () => void
}

/**
 * 取消訂單流程 hook（待付款 / 處理中 / 準備出貨）
 *
 * 流程：統一先呼叫 cancelable API
 * - 成功（可取消）→ DialogConfirmCancelOrder（確認取消訂單）
 * - 失敗，依 error code 判斷：
 *   - 16008 / 16009 → DialogReadyForShipmentCannotCancel（商品準備出貨，無法取消訂單）
 *   - 16010 + 待付款 → DialogUnsupportedSelfCancel（此訂單不支援自行取消）
 *   - 16010 + 待出貨 → DialogManualRefundRequired（此訂單需人工退款）
 *
 * 2C/2B 文案差異透過 isMerchant（= environment === 'merchant'）區分
 */
export function useCancelOrder({ environment, countryCode, projectCode, campaignCodeFromUrl, onCancelOrderClick }: UseCancelOrderParams) {
  const { t } = useTranslation()
  const history = useHistory()
  const { openSnackbar } = useSnackbar<SnackbarVariantsProps>()

  const isMerchantEnvironment = environment === 'merchant'
  const isCampaignEnvironment = environment === 'campaign'
  const isWordPressEnvironment = environment === 'wordPress'

  const { openDialog: openReadyForShipmentCannotCancelDialog } = useDialog<DialogReadyForShipmentCannotCancelExtraProps>(DIALOG_READY_FOR_SHIPMENT_CANNOT_CANCEL)
  const { openDialog: openManualRefundRequiredDialog } = useDialog<DialogManualRefundRequiredExtraProps>(DIALOG_MANUAL_REFUND_REQUIRED)
  const { openDialog: openConfirmCancelOrderDialog } = useDialog<DialogConfirmCancelOrderExtraProps>(DIALOG_CONFIRM_CANCEL_ORDER)
  const { openDialog: openUnsupportedSelfCancelDialog } = useDialog<DialogUnsupportedSelfCancelExtraProps>(DIALOG_UNSUPPORTED_SELF_CANCEL)

  const { trigger: triggerGetPaymentTransactionCancelable } = useApiGetPaymentTransactionCancelable()
  const { trigger: triggerECommerceGetPaymentTransactionCancelable } = useECommerceGetPaymentTransactionCancelable()
  const { trigger: triggerCancelOrder } = useApiCancelPaymentTransaction()
  const { trigger: triggerECommerceCancelPaymentTransaction } = useECommerceCancelPaymentTransaction()

  async function handleCancelOrder(paymentTransactionId: string, displayStatus?: DisplayStatusType) {
    const isMerchant = isMerchantEnvironment

    try {
      if (isWordPressEnvironment) {
        await triggerECommerceGetPaymentTransactionCancelable({ id: paymentTransactionId, country_code: countryCode })
      }
      else {
        await triggerGetPaymentTransactionCancelable({ id: paymentTransactionId })
      }
      openConfirmCancelOrderDialog({
        transactionId: paymentTransactionId,
        onConfirm: async () => {
          try {
            if (isWordPressEnvironment) {
              await triggerECommerceCancelPaymentTransaction({ id: paymentTransactionId, country_code: countryCode })
              mutateAllECommerceOrders?.()
            }
            else {
              await triggerCancelOrder({ id: paymentTransactionId })
            }
            onCancelOrderClick?.()
            if (isCampaignEnvironment) {
              history.push(`/campaigns/${campaignCodeFromUrl}/my-account/order?tabValue=cancelled&focusedTransactionId=${paymentTransactionId}`)
            }
            if (isMerchantEnvironment) {
              history.push(`/merchant/${campaignCodeFromUrl}/my-account/order?tabValue=cancelled&focusedTransactionId=${paymentTransactionId}`)
            }
            if (isWordPressEnvironment) {
              wordPressNavigate(`/my-account/order?tabValue=cancelled&focusedTransactionId=${paymentTransactionId}`)
            }
            openSnackbar({
              message: t('orderDetail.orderCancelled', { defaultValue: '訂單已取消' }),
              severity: 'success',
            })
          }
          catch (error) {
            console.error('[useCancelOrder] cancel payment transaction failed', error)
            openSnackbar({
              message: t('orderDetail.cancelOrderFailed', { defaultValue: '取消訂單失敗' }),
              severity: 'error',
            })
          }
        },
      })
    }
    catch (error) {
      const errorCode = error instanceof AxiosError
        ? (error as AxiosError<ResponseErrorGenericsV2>).response?.data.error.code
        : undefined

      if (errorCode === CANCELABLE_ERROR_CODES.NONCANCELLABLE_SHIPMENT_READY || errorCode === CANCELABLE_ERROR_CODES.NONCANCELLABLE_SHIPMENT_CREATING) {
        openReadyForShipmentCannotCancelDialog({ isMerchant })
        return
      }

      if (errorCode === CANCELABLE_ERROR_CODES.NONCANCELLABLE_VIRTUAL_ACCOUNT) {
        if (displayStatus === DISPLAY_STATUS.PENDING_PAYMENT) {
          openUnsupportedSelfCancelDialog({ isMerchant })
        }
        else {
          openManualRefundRequiredDialog({ isMerchant, projectCode, countryCode })
        }
        return
      }

      openSnackbar({
        message: t('orderDetail.cancelOrderFailed', { defaultValue: '取消訂單失敗' }),
        severity: 'error',
      })
    }
  }

  return { handleCancelOrder }
}
